import styled, { css } from 'styled-components'

export interface LegendItemStyledProps {
  $active?: boolean
  $clickable?: boolean
  $size?: 'small' | 'medium' | 'large'
}

const sizeStyles = {
  small: css`
    gap: 6px;
    padding: 4px 0;

    .legend-marker {
      width: 12px;
      height: 12px;
      min-width: 12px;

      &.line {
        height: 2px;
        width: 16px;
        min-width: 16px;
      }
    }

    .legend-label {
      font-size: ${({ theme }) => theme.typography.fontSize.micro};
    }

    .legend-description {
      font-size: ${({ theme }) => theme.typography.fontSize.label};
    }
  `,
  medium: css`
    gap: 8px;
    padding: 6px 0;

    .legend-marker {
      width: 16px;
      height: 16px;
      min-width: 16px;

      &.line {
        height: 3px;
        width: 20px;
        min-width: 20px;
      }
    }

    .legend-label {
      font-size: ${({ theme }) => theme.typography.fontSize.small};
    }

    .legend-description {
      font-size: ${({ theme }) => theme.typography.fontSize.micro};
    }
  `,
  large: css`
    gap: 12px;
    padding: 8px 0;

    .legend-marker {
      width: 20px;
      height: 20px;
      min-width: 20px;

      &.line {
        height: 4px;
        width: 24px;
        min-width: 24px;
      }
    }

    .legend-label {
      font-size: ${({ theme }) => theme.typography.fontSize.body};
    }

    .legend-description {
      font-size: ${({ theme }) => theme.typography.fontSize.small};
    }
  `,
}

export const Container = styled.div.withConfig({
  shouldForwardProp: (prop) => !prop.startsWith('$'),
})<LegendItemStyledProps>`
  display: flex;
  align-items: flex-start;
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};
  opacity: ${({ $active }) => ($active === false ? 0.5 : 1)};
  transition: opacity 0.2s ease;

  ${({ $size = 'medium' }) => sizeStyles[$size]}

  ${({ $clickable }) =>
    $clickable &&
    css`
      &:hover {
        opacity: 0.8;
      }

      &:active {
        opacity: 0.6;
      }
    `}
`

export const Marker = styled.div.withConfig({
  shouldForwardProp: (prop) => !prop.startsWith('$'),
})<{
  $color?: string
  $shape?: 'circle' | 'square' | 'line' | 'custom'
}>`
  background: ${({ $color }) => $color || 'currentColor'};
  border-radius: ${({ $shape }) => {
    switch ($shape) {
      case 'circle':
        return '50%'
      case 'square':
        return '2px'
      case 'line':
        return '1px'
      default:
        return '2px'
    }
  }};
  flex-shrink: 0;

  ${({ $shape }) =>
    $shape === 'custom' &&
    css`
      background: none;
      display: flex;
      align-items: center;
      justify-content: center;

      svg,
      img {
        width: 100%;
        height: 100%;
        object-fit: contain;
      }
    `}
`

export const Content = styled.div`
  flex: 1;
  min-width: 0;
`

export const Label = styled.div`
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${({ theme }) => theme.colors.text.primary};
  line-height: 1.4;
  margin: 0;
`

export const Description = styled.div`
  color: ${({ theme }) => theme.colors.text.secondary};
  line-height: 1.3;
  margin: 2px 0 0 0;
`
